import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Spinner } from 'reactstrap';
import { useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';
import { createOrg } from '../api/onboardingApi';
import { trackTrialStarted } from '../services/metaPixel';
import logoSrc from '../assets/marginpal_logo.svg';

// First-tenant page. Renders for signed-in users who don't have an active
// organisation yet — i.e. brand-new sign-ups, since we disabled Clerk's
// "Force Organization Membership" task and locked down user-driven org
// creation in the Clerk Dashboard. The flow:
//
//   1. User submits company name.
//   2. We POST to /api/Onboarding/CreateOrg, which uses the Clerk Backend
//      API to create the org and add the user as its admin.
//   3. We call Clerk's setActive({ organization }) so the next JWT carries
//      the new org_id claim. TenantMiddleware then JIT-creates the matching
//      Company + User row on the next request.
//   4. Redirect to /dashboard — the new company already has a 14-day no-card
//      trial (created server-side in CreateOrg), so the user is entitled and
//      lands in the app rather than on the paywall.
//
// Users who already have an active org get bounced to /dashboard — there's
// nothing for them here. (Joining additional orgs as a Worker happens via
// the invite flow, not this page.)
export default function OnboardingPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useUser();
    const { organization } = useOrganization();
    const { setActive, userMemberships, isLoaded: listLoaded } = useOrganizationList({
        userMemberships: true,
    });

    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // If they already have an org, get them out of here.
    useEffect(() => {
        if (organization) {
            navigate('/dashboard', { replace: true });
            return;
        }
        if (listLoaded && userMemberships?.data?.length > 0 && setActive) {
            // Memberships exist (e.g. Worker invited to another org) but
            // none active — promote the first one and exit. This is the
            // same fallback RequireOrg used to do; running it here keeps
            // the Worker invitee out of the "create your own org" form.
            setActive({ organization: userMemberships.data[0].organization.id });
        }
    }, [organization, listLoaded, userMemberships, setActive, navigate]);

    async function handleSubmit(e) {
        e.preventDefault();
        const trimmed = name.trim();
        if (trimmed.length < 2) {
            setError('Company name must be at least 2 characters.');
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const { orgId } = await createOrg(trimmed);

            // Fire the StartTrial conversion — the top-of-funnel event the ad
            // campaign optimises on now that trials don't go through checkout.
            // Dedups with the server CAPI copy (fired from CreateOrg) via orgId.
            trackTrialStarted(orgId);

            // Activate the new org so the next JWT carries org_id and
            // TenantMiddleware can resolve a tenant context. Clerk's
            // setActive triggers a session refresh transparently.
            await setActive({ organization: orgId });

            // New signups get a 14-day no-card trial at creation, so they're
            // already entitled — send them straight into the app, not the
            // paywall. Invalidate the cached billing status first so
            // RequireSubscription sees the fresh trial, not a stale "no sub".
            await queryClient.invalidateQueries({ queryKey: ['billingStatus'] });
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(err?.message || 'Failed to create organisation.');
            setSubmitting(false);
        }
    }

    // Reuse the .login-page wrapper that signInPage uses so visual continuity
    // is maintained from sign-up → onboarding (same centred shell, same
    // background tone, same responsive padding). The card itself is a plain
    // .surface so it picks up the shared elevation + border-radius treatment.
    return (
        <div className="login-page">
            <div className="surface p-4 p-md-5 onboarding-card" style={{ maxWidth: 460, width: '100%' }}>
                <div className="text-center mb-4">
                    <img
                        src={logoSrc}
                        alt="MarginPal"
                        style={{ height: 48, width: 48 }}
                        className="mb-3"
                    />
                    <h1 className="fw-bold mb-1" style={{ fontSize: '1.5rem' }}>
                        Welcome{user?.firstName ? `, ${user.firstName}` : ''}
                    </h1>
                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                        Let's set up your company. You can change this later in Settings.
                    </p>
                </div>
                <form onSubmit={handleSubmit}>
                    <label className="form-label fw-medium small mb-2">Company name</label>
                    <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Acme Building Ltd"
                        autoFocus
                        disabled={submitting}
                        maxLength={100}
                        bsSize="lg"
                    />
                    {error && <div className="text-danger small mt-2">{error}</div>}
                    <Button
                        type="submit"
                        color="primary"
                        size="lg"
                        className="mt-4 w-100 d-inline-flex align-items-center justify-content-center gap-2 fw-semibold"
                        disabled={submitting || name.trim().length < 2}
                    >
                        {submitting ? <Spinner size="sm" /> : null}
                        {submitting ? 'Creating…' : 'Create company'}
                    </Button>
                </form>
            </div>
        </div>
    );
}
